import { motion } from "framer-motion";
import { Award, BadgeCheck, Briefcase, CalendarDays, Clock, Globe, MapPin, ShieldCheck, Star, ThumbsUp, Zap } from "lucide-react";
import { PROFILE, AI_MATCHING, idr } from "../data/kabelul";

export default function QualificationsPage({ setPage }) {
  return (
    <div className="page-inner">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">QUALIFICATION PASSPORT · VERIFIED PROFILE</p>
          <h1>Your Passport</h1>
          <p className="subtitle">A verified summary of your skills, certifications, and work history.</p>
        </div>
        <button data-testid="passport-share-button" className="secondary-button" onClick={() => setPage("opportunities")}>
          <Briefcase size={15} /> Find matching work
        </button>
      </motion.div>

      <section className="k-passport-hero" data-testid="qualifications-passport-hero">
        <img src={PROFILE.photo} alt={PROFILE.name} className="k-avatar-xl" />
        <div className="k-passport-info">
          <div className="k-passport-name">
            <h2>{PROFILE.name}</h2>
            {PROFILE.verified && <BadgeCheck size={19} className="verified" />}
          </div>
          <p className="k-passport-title">{PROFILE.title}</p>
          <div className="k-passport-meta">
            <span><MapPin size={12} /> {PROFILE.location}</span>
            <span><CalendarDays size={12} /> Member since {PROFILE.memberSince}</span>
            <span><Globe size={12} /> {PROFILE.languages.join(" · ")}</span>
          </div>
          <div className="k-tier-badge">
            <ShieldCheck size={14} /> {PROFILE.tier}
            <span className="k-tier-mini">
              {Array.from({ length: PROFILE.tierMax }).map((_, i) => (
                <i key={i} className={i < PROFILE.tierLevel ? "on" : ""} />
              ))}
            </span>
          </div>
        </div>
        <div className="k-passport-stats">
          <div><small>RATING</small><b>{PROFILE.rating} ★</b><small>{PROFILE.reviews} reviews</small></div>
          <div><small>JOBS</small><b>{PROFILE.completedJobs}</b><small>Completed</small></div>
          <div><small>RATE</small><b>{idr(PROFILE.hourlyRate)}</b><small>per hour</small></div>
          <div><small>AVAILABILITY</small><b><Clock size={13} style={{ verticalAlign: -2 }} /> Open</b><small>{PROFILE.availability}</small></div>
        </div>
      </section>

      <div className="content-grid" style={{ marginTop: 22 }}>
        <section className="panel k-panel">
          <div className="panel-head">
            <div>
              <div className="section-kicker">VERIFIED SKILLS</div>
              <h3>Your capabilities</h3>
            </div>
            <span className="k-count">{PROFILE.skills.filter(s => s.verified).length} verified · {PROFILE.skills.filter(s => !s.verified).length} in progress</span>
          </div>
          <div className="k-skills-list">
            {PROFILE.skills.map((s) => (
              <div className={`k-skill-row ${s.inProgress ? "in-progress" : ""}`} key={s.name}>
                <div>
                  <b>{s.name}</b>
                  <small>{s.endorsements} peer endorsements</small>
                </div>
                <div className="k-skill-side">
                  <span className={`k-level ${s.verified ? "ok" : "warn"}`}>
                    {s.verified ? <BadgeCheck size={11} /> : <Clock size={11} />} {s.level}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="panel k-panel">
          <div className="panel-head">
            <div>
              <div className="section-kicker">AI MATCHING · OPPORTUNITY VIEW</div>
              <h3>What your Passport unlocks</h3>
            </div>
          </div>
          <div className="k-unlock-summary">
            <div className="k-unlock-num">{AI_MATCHING.eligible}</div>
            <div>
              <b>opportunities eligible today</b>
              <small>Based on verified skills, location, and availability.</small>
            </div>
          </div>
          <div className="k-potential">
            <b>Complete a course to unlock more</b>
            {AI_MATCHING.potentialIfLearn.map((x) => (
              <div className="k-potential-row" key={x.skill}>
                <Zap size={12} />
                <span>{x.skill}</span>
                <em>+{x.unlocks}</em>
              </div>
            ))}
            <button className="secondary-button" onClick={() => setPage("learning")} data-testid="qualifications-open-learning">
              Open Learning
            </button>
          </div>
        </section>
      </div>

      <section className="section-block">
        <div className="section-title">
          <div>
            <div className="section-kicker">CERTIFICATIONS · TAMPER-EVIDENT LEDGER</div>
            <h2>Your certifications</h2>
          </div>
          <span className="k-count">{PROFILE.certifications.length} verified</span>
        </div>
        <div className="k-cert-grid">
          {PROFILE.certifications.map((c, i) => (
            <div className="k-cert-card" key={c.id} data-testid={`certification-card-${i}`}>
              <div className="k-cert-icon"><Award size={19} /></div>
              <div>
                <b>{c.title}</b>
                <small>{c.issuer}</small>
                <div className="k-cert-meta">
                  <span>Issued {c.year}</span>
                  <span className="cert-id">{c.id}</span>
                </div>
              </div>
              <BadgeCheck size={18} className="verified" />
            </div>
          ))}
        </div>
      </section>

      <section className="section-block">
        <div className="section-title">
          <div>
            <div className="section-kicker">ABOUT</div>
            <h2>Bio</h2>
          </div>
        </div>
        <div className="k-bio">
          <p>{PROFILE.bio}</p>
          <div className="k-bio-endorsements">
            <ThumbsUp size={14} />
            <span>151 total peer endorsements</span>
            <Star size={14} />
            <span>Recognised by 12 Indonesian employers</span>
          </div>
        </div>
      </section>
    </div>
  );
}
